import { Component, TemplateRef, ViewChild, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BehaviorSubject,
  EMPTY,
  catchError,
  finalize,
  first,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { Constants } from 'src/app/core/constants';
import { InventoryItemStatuses } from 'src/app/core/enums/inventory-item-statuses.enum';
import { getCurrencySymbol } from 'src/app/core/utils/currency-symbol.util';
import {
  getInventoryItemTypeLabelKey,
  isInventoryItemType,
} from 'src/app/core/enums/inventory-item-type.enum';
import { CustomFile } from 'src/app/core/models/customFile.interface';
import { InventoryItemImageSlot } from 'src/app/core/models/inventory/inventory-item-image-slot.interface';
import { InventoryItemChange } from 'src/app/core/models/inventory/inventory-item-change.interface';
import { InventoryItem } from 'src/app/core/models/inventory/inventory-item.interface';
import { UnitOfMeasureLookup } from 'src/app/core/models/inventory/unit-of-measure-lookup.interface';
import {
  InventoryItemOverviewTab,
  InventoryItemOverviewTabKey,
} from 'src/app/core/models/inventory/inventory-item-overview-view-model.interface';
import { FileService } from 'src/app/core/services/file.service';
import { InventoryItemService } from 'src/app/core/services/inventory-item.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { Util } from 'src/app/core/utils/util';
import {
  InventoryItemSearchOption,
  buildInventoryItemOption,
} from 'src/app/core/utils/inventory-item-search.util';

// Soft cap for the in-page inventory-item options list used by the composite
// component picker. Mirrors the page-size used by `sales-order-overview` so the
// child-picker shows the full active catalog without paging the user through
// search results.
const COMPONENT_PICKER_PAGE_SIZE = 200;

interface InventoryItemHeroField {
  id: string;
  labelKey: string;
  value?: string;
  valueKey?: string;
  secondaryValue?: string;
  valueClass?: string;
  badgeClass?: string;
}

interface InventoryItemHeroMetric {
  id: string;
  labelKey: string;
  value?: number | string | null;
  valueFormat: 'number' | 'percent' | 'date';
  emptyValue?: string;
  supportText?: string;
  supportTextKey?: string;
  cardClass?: string;
  valueClass?: string;
}

@Component({
  selector: 'app-inventory-item-overview',
  templateUrl: './inventory-item-overview.component.html',
  styleUrl: './inventory-item-overview.component.scss',
  standalone: false,
})
export class InventoryItemOverviewComponent {
  private readonly inventoryItemService = inject(InventoryItemService);
  private readonly fileService = inject(FileService);
  private readonly toastService = inject(ToastService);

  @ViewChild('concurrencyWarningTpl') private concurrencyWarningTpl?: TemplateRef<unknown>;

  // Full ordered set of overview tabs. The Components tab is *only* present when the
  // item is composite (recomputed in `syncInventoryItemState` so toggling `isComposite`
  // on the Info tab makes the Components tab appear without a route reload).
  private readonly allOverviewTabs: InventoryItemOverviewTab[] = [
    { id: 'info', labelKey: 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.TABS.INFO' },
    { id: 'components', labelKey: 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.TABS.COMPONENTS' },
    { id: 'finances', labelKey: 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.TABS.FINANCE' },
    { id: 'warehouse', labelKey: 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.TABS.LOGISTICS' },
    { id: 'identification', labelKey: 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.TABS.IDENTIFICATION' },
    { id: 'files', labelKey: 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.TABS.FILES' },
    { id: 'description', labelKey: 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.TABS.DESCRIPTION' },
    { id: 'unitPricing', labelKey: 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.TABS.UOM_AND_PRICES' },
  ];
  public overviewTabs: InventoryItemOverviewTab[] = this.allOverviewTabs.filter(
    (tab) => tab.id !== 'components',
  );
  public readonly imageExtensions = Constants.ImageExtensions;
  public readonly notFound = Constants.NotFound;

  public isLoading = false;
  public companyId = '';
  public itemId = '';
  public inventoryItemsBackUrl = '';
  public itemFilesPath = '';
  public galleryImagesPath = '';
  public activeOverviewTab: InventoryItemOverviewTabKey = 'info';
  public itemTitle = this.notFound;
  public itemSubtitle = '';
  public infoTabReloadToken = '';
  public filesTabReloadToken = 0;
  public isHistoryPanelOpen = false;
  public isHistoryLoading = false;
  public inventoryItemChanges: InventoryItemChange[] = [];
  public heroKeyInfoFields: InventoryItemHeroField[] = [];
  public heroMetricCards: InventoryItemHeroMetric[] = [];

  public activeImageSlotId = 'title';
  public activeImageUrl = this.notFound;
  public activeImageExtension?: string;
  public thumbnailSlots: InventoryItemImageSlot[] = [];
  public nextAvailableImageSlotId?: string;
  public nextImageUploadPath = '';
  public canAddMoreImages = true;
  public isImageViewerOpen = false;
  public unitsOfMeasureOptions: UnitOfMeasureLookup[] = [];
  public inventoryItemOptions: InventoryItemSearchOption[] = [];

  public readonly inventoryItem$ = new BehaviorSubject<InventoryItem | null>(null);

  private readonly imageSlotIds = ['title', 'image-2', 'image-3', 'image-4'] as const;

  constructor() {
    const route = inject(ActivatedRoute);

    route.params
      .pipe(
        first(),
        tap(() => {
          this.isLoading = true;
        }),
        tap((params) => {
          this.itemId = params['id'];
          this.companyId = route.parent?.parent?.snapshot.params['id'];
          this.inventoryItemsBackUrl = `/companies/${this.companyId}/crm/goods`;
          this.itemFilesPath = `companies/${this.companyId}/inventory-items/${this.itemId}`;
          this.galleryImagesPath = `${this.itemFilesPath}/gallery`;
          this.infoTabReloadToken = this.itemId;
        }),
        switchMap((params) =>
          forkJoin({
            inventoryItem: this.inventoryItemService.getInventoryItem(this.companyId, params['id']),
            unitsOfMeasureOptions: this.inventoryItemService.getUnitsOfMeasureLookup(this.companyId),
            // Page of inventory items used by the composite child picker. Fetched here so
            // the Components tab can render synchronously when the user toggles it on.
            inventoryItems: this.inventoryItemService.getCompanyInventoryItemsPaged(this.companyId, {
              filters: [],
              page: 1,
              pageSize: COMPONENT_PICKER_PAGE_SIZE,
            }),
          }),
        ),
        tap(({ inventoryItem, unitsOfMeasureOptions, inventoryItems }) => {
          this.unitsOfMeasureOptions = unitsOfMeasureOptions;
          this.inventoryItemOptions = inventoryItems.items.map(buildInventoryItemOption);
          this.syncInventoryItemState(inventoryItem);
        }),
        switchMap(() => this.loadImageGallery()),
        tap((slots) => this.syncGalleryState(slots)),
        catchError(() => EMPTY),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  public onOverviewTabChange(tabId: string | number): void {
    if (typeof tabId !== 'string') {
      throw new Error('Overview tab id must be a string.');
    }

    this.activeOverviewTab = tabId as InventoryItemOverviewTabKey;
  }

  public onSelectImage(slotId: string): void {
    const selectedSlot = this.thumbnailSlots.find((slot) => slot.id === slotId);
    if (!selectedSlot) {
      return;
    }

    this.activeImageSlotId = selectedSlot.id;
    this.activeImageUrl = selectedSlot.imageUrl;
    this.activeImageExtension = selectedSlot.extension;
  }

  public onAdditionalImageUpload(event: CustomFile): void {
    this.upsertImageSlot(event);
    this.filesTabReloadToken += 1;
  }

  public onAdditionalImageDelete(): void {
    this.filesTabReloadToken += 1;
    this.reloadImageGalleryState();
  }

  public onImageFilesChanged(): void {
    this.filesTabReloadToken += 1;
    this.reloadImageGalleryState();
  }

  public onOpenImageViewer(slotId: string): void {
    this.onSelectImage(slotId);
    this.isImageViewerOpen = true;
  }

  public onCloseImageViewer(): void {
    this.isImageViewerOpen = false;
  }

  public onInventoryItemChange(inventoryItem: InventoryItem): void {
    this.syncInventoryItemState(inventoryItem);
  }

  public openHistoryPanel(): void {
    if (!this.companyId || !this.itemId) {
      return;
    }

    this.isHistoryPanelOpen = true;
    this.isHistoryLoading = true;
    this.inventoryItemService
      .getInventoryItemChanges(this.companyId, this.itemId)
      .pipe(
        first(),
        tap((changes) => {
          this.inventoryItemChanges = changes;
        }),
        catchError(() => {
          this.inventoryItemChanges = [];
          return of([]);
        }),
        finalize(() => {
          this.isHistoryLoading = false;
        }),
      )
      .subscribe();
  }

  public closeHistoryPanel(): void {
    this.isHistoryPanelOpen = false;
  }

  public archiveInventoryItem(): void {
    this.inventoryItemService
      .archiveInventoryItem(this.companyId, this.itemId)
      .pipe(
        first(),
        tap((inventoryItem) => {
          this.syncInventoryItemState(inventoryItem);
        }),
      )
      .subscribe();
  }

  public unarchiveInventoryItem(): void {
    this.inventoryItemService
      .unarchiveInventoryItem(this.companyId, this.itemId)
      .pipe(
        first(),
        tap((inventoryItem) => {
          this.syncInventoryItemState(inventoryItem);
        }),
      )
      .subscribe();
  }

  public onConcurrencyConflict(): void {
    if (!this.concurrencyWarningTpl) {
      return;
    }

    this.toastService.show({
      template: this.concurrencyWarningTpl,
      className: 'toast-warning',
    });
  }

  private syncInventoryItemState(inventoryItem: InventoryItem): void {
    this.inventoryItem$.next(inventoryItem);
    this.itemTitle = inventoryItem.displayName || inventoryItem.number || this.notFound;
    this.itemSubtitle = inventoryItem.displayName2 ?? '';
    this.heroKeyInfoFields = this.buildHeroKeyInfoFields(inventoryItem);
    this.heroMetricCards = this.buildHeroMetricCards(inventoryItem);
    this.refreshOverviewTabs(inventoryItem);
  }

  // Recomputes the visible overview-tab list from the latest inventory-item state.
  // The Components tab is registered only when `isComposite === true`; when the user
  // toggles the flag off and the active tab disappears, we fall back to the Info tab
  // so the user never lands on a hidden tab id.
  private refreshOverviewTabs(inventoryItem: InventoryItem): void {
    const isComposite = inventoryItem.isComposite === true;
    this.overviewTabs = this.allOverviewTabs.filter(
      (tab) => tab.id !== 'components' || isComposite,
    );
    if (!isComposite && this.activeOverviewTab === 'components') {
      this.activeOverviewTab = 'info';
    }
  }

  private buildHeroKeyInfoFields(item: InventoryItem): InventoryItemHeroField[] {
    const typeKey = isInventoryItemType(item.itemType)
      ? getInventoryItemTypeLabelKey(item.itemType)
      : undefined;
    const statusKey = this.resolveStatusKey(item.status);

    return [
      {
        id: 'name',
        labelKey: 'COMPONENTS.GOODS.FIELDS.NAME',
        value: item.displayName ?? '',
        secondaryValue: item.displayName2 ?? '',
      },
      {
        id: 'sku',
        labelKey: 'COMPONENTS.GOODS.FIELDS.SKU',
        value: item.number ?? '',
        valueClass: 'inventory-item-overview__value--mono',
      },
      {
        id: 'gtin',
        labelKey: 'COMPONENTS.GOODS.FIELDS.GTIN',
        value: item.gtin ?? '',
        valueClass: 'inventory-item-overview__value--mono',
      },
      {
        id: 'category',
        labelKey: 'COMPONENTS.GOODS.FIELDS.CATEGORY',
        value: item.categoryTitle ?? '',
      },
      {
        id: 'type',
        labelKey: 'COMPONENTS.GOODS.FIELDS.TYPE',
        value: item.itemType ?? '',
        valueKey: typeKey,
        badgeClass: 'inventory-item-overview__badge inventory-item-overview__badge--type',
      },
      {
        id: 'manufacturer',
        labelKey: 'COMPONENTS.GOODS.FIELDS.MANUFACTURER',
        value: item.manufacturer ?? '',
      },
      {
        id: 'model',
        labelKey: 'COMPONENTS.GOODS.FIELDS.MODEL',
        value: item.model ?? '',
      },
      {
        id: 'status',
        labelKey: 'COMPONENTS.GOODS.FIELDS.STATUS',
        valueKey: statusKey,
        badgeClass: this.resolveStatusBadgeClass(item.status),
      },
    ].filter(
      (field) =>
        field.id === 'name' ||
        field.id === 'sku' ||
        field.id === 'category' ||
        field.id === 'type' ||
        field.id === 'status' ||
        this.hasValue(field.value),
    );
  }

  private buildHeroMetricCards(item: InventoryItem): InventoryItemHeroMetric[] {
    const currencySymbol = getCurrencySymbol(item.currencyCode);
    const unitLabel = item.unit ?? '';
    const sourceLabelKey = item.businessCentralId
      ? 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.SOURCES.BUSINESS_CENTRAL'
      : 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.SOURCES.ORGLINES';

    return [
      {
        id: 'stock',
        labelKey: 'COMPONENTS.GOODS.FIELDS.STOCK',
        value: item.stock,
        valueFormat: 'number',
        emptyValue: '0',
        supportText: unitLabel,
      },
      {
        id: 'price',
        labelKey: 'COMPONENTS.GOODS.FIELDS.SALES_PRICE',
        value: item.unitPrice,
        valueFormat: 'number',
        emptyValue: '0',
        supportText: currencySymbol,
        cardClass: 'inventory-item-overview__metric-card--price',
        valueClass: 'inventory-item-overview__metric-value--price',
      },
      {
        id: 'cost',
        labelKey: 'COMPONENTS.GOODS.FIELDS.COST',
        value: item.unitCost,
        valueFormat: 'number',
        emptyValue: '0',
        supportText: currencySymbol,
      },
      {
        id: 'margin',
        labelKey: 'COMPONENTS.GOODS.FIELDS.MARGIN',
        value: item.marginPercentage,
        valueFormat: 'percent',
        emptyValue: '0%',
        supportTextKey: 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.MARGIN_SUPPORT',
        cardClass: 'inventory-item-overview__metric-card--margin',
        valueClass: 'inventory-item-overview__metric-value--margin',
      },
      {
        id: 'updated',
        labelKey: 'COMPONENTS.GOODS.FIELDS.UPDATED_AT',
        value: item.lastModifiedDateTime,
        valueFormat: 'date',
        emptyValue: this.notFound,
        supportTextKey: sourceLabelKey,
        valueClass: 'inventory-item-overview__metric-value--sm',
      },
    ];
  }

  private hasValue(value?: string | null): boolean {
    return typeof value === 'string' && value.trim().length > 0;
  }

  private resolveStatusKey(status?: InventoryItemStatuses): string | undefined {
    if (status === undefined || status === null) {
      return undefined;
    }

    const enumKey = InventoryItemStatuses[status];
    if (!enumKey) {
      return undefined;
    }

    return `INVENTORY_ITEM_STATUSES.${enumKey.toUpperCase()}`;
  }

  private resolveStatusBadgeClass(status?: InventoryItemStatuses): string | undefined {
    if (status === undefined || status === null) {
      return undefined;
    }

    return status === InventoryItemStatuses.Blocked
      ? 'inventory-item-overview__badge inventory-item-overview__badge--status-blocked'
      : 'inventory-item-overview__badge inventory-item-overview__badge--status-active';
  }

  private loadImageGallery(): Observable<InventoryItemImageSlot[]> {
    return forkJoin({
      rootKeys: this.fileService.listFiles(this.itemFilesPath).pipe(catchError(() => of([]))),
      galleryKeys: this.fileService.listFiles(this.galleryImagesPath).pipe(catchError(() => of([]))),
    }).pipe(
      map(({ rootKeys, galleryKeys }) => this.buildImageSlots(rootKeys, galleryKeys)),
      switchMap((slots) => {
        if (!slots.length) {
          return of(slots);
        }

        return forkJoin(
          slots.map((slot) =>
            this.fileService.getFileUrl(slot.imageUrl, true).pipe(
              map((imageUrl) => ({
                ...slot,
                imageUrl,
              })),
            ),
          ),
        );
      }),
    );
  }

  private buildImageSlots(rootKeys: string[], galleryKeys: string[]): InventoryItemImageSlot[] {
    const rootPathPrefix = `${this.itemFilesPath}/`;
    const galleryPathPrefix = `${this.galleryImagesPath}/`;
    const slotById = new Map<string, InventoryItemImageSlot>();

    for (const key of rootKeys) {
      if (!key.startsWith(rootPathPrefix) || key.startsWith(galleryPathPrefix)) {
        continue;
      }

      const fileName = key.slice(rootPathPrefix.length);
      const extension = this.resolveFileExtension(fileName);
      const slotId = extension ? fileName.slice(0, -extension.length) : fileName;

      if (slotId !== 'title') {
        continue;
      }

      slotById.set(slotId, {
        id: slotId,
        imageUrl: key,
        extension,
      });
    }

    for (const key of galleryKeys) {
      if (!key.startsWith(galleryPathPrefix)) {
        continue;
      }

      const fileName = key.slice(galleryPathPrefix.length);
      const extension = this.resolveFileExtension(fileName);
      const slotId = extension ? fileName.slice(0, -extension.length) : fileName;

      if (!this.imageSlotIds.includes(slotId as (typeof this.imageSlotIds)[number]) || slotId === 'title') {
        continue;
      }

      slotById.set(slotId, {
        id: slotId,
        imageUrl: key,
        extension,
      });
    }

    return this.sortImageSlots(Array.from(slotById.values()));
  }

  private resolveFileExtension(fileName: string): string | undefined {
    const extensionIndex = fileName.lastIndexOf('.');
    if (extensionIndex <= 0) {
      return undefined;
    }

    return fileName.slice(extensionIndex).toLowerCase();
  }

  private syncGalleryState(slots: InventoryItemImageSlot[]): void {
    this.thumbnailSlots = slots;
    this.canAddMoreImages = slots.length < this.imageSlotIds.length;
    this.nextAvailableImageSlotId = this.imageSlotIds.find(
      (slotId) => !slots.some((slot) => slot.id === slotId),
    );
    this.nextImageUploadPath = this.resolveImageUploadPath(this.nextAvailableImageSlotId);

    if (slots.length === 0) {
      this.activeImageSlotId = this.nextAvailableImageSlotId ?? 'title';
      this.activeImageUrl = this.notFound;
      this.activeImageExtension = undefined;
      return;
    }

    const activeSlot = slots.find((slot) => slot.id === this.activeImageSlotId) ?? slots[0];
    this.activeImageSlotId = activeSlot.id;
    this.activeImageUrl = activeSlot.imageUrl;
    this.activeImageExtension = activeSlot.extension;
  }

  private upsertImageSlot(event: CustomFile): void {
    const extension = Util.getExtensionFromFileTitle(event.file.name);
    const normalizedExtension = extension ? `.${extension}` : undefined;
    const slotId = event.title;

    if (!slotId || !normalizedExtension) {
      return;
    }

    const nextSlot: InventoryItemImageSlot = {
      id: slotId,
      imageUrl: event.upload?.key ?? this.activeImageUrl,
      extension: normalizedExtension,
    };

    const updatedSlots = [
      ...this.thumbnailSlots.filter((slot) => slot.id !== slotId),
      {
        ...nextSlot,
        imageUrl: globalThis.URL?.createObjectURL(event.file) ?? this.notFound,
      },
    ];

    this.syncGalleryState(this.sortImageSlots(updatedSlots));
    this.onSelectImage(slotId);
  }

  private resolveImageUploadPath(slotId?: string): string {
    if (slotId === 'title') {
      return this.itemFilesPath;
    }

    return this.galleryImagesPath;
  }

  private reloadImageGalleryState(): void {
    this.loadImageGallery()
      .pipe(
        first(),
        tap((slots) => this.syncGalleryState(slots)),
        catchError(() => EMPTY),
      )
      .subscribe();
  }

  private sortImageSlots(slots: InventoryItemImageSlot[]): InventoryItemImageSlot[] {
    return [...slots].sort(
      (left, right) =>
        this.imageSlotIds.indexOf(left.id as (typeof this.imageSlotIds)[number]) -
        this.imageSlotIds.indexOf(right.id as (typeof this.imageSlotIds)[number]),
    );
  }
}
